import { useEffect, useMemo, useState } from "react";
import { format, addDays, addWeeks, addMonths, startOfWeek, startOfMonth, endOfMonth, endOfWeek, isSameDay, isSameMonth } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { api, formatApiError, formatDuration } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plus, Ban, Trash2 } from "lucide-react";
import { PAYMENT_LABELS, PAYMENT_STYLES } from "./DepositsPanel";

const STATUS_CHIP = {
  pending: "bg-[var(--brand-accent)]/25 text-[var(--brand-dark)]",
  confirmed: "bg-[#336633]/15 text-[#336633]",
  cancelled: "bg-black/5 text-[var(--brand-muted)] line-through",
};
const STATUS_LABELS = { pending: "En attente", confirmed: "Confirmé", cancelled: "Annulé" };

const toKey = (d) => format(d, "yyyy-MM-dd");
const today = () => new Date(new Date().setHours(0, 0, 0, 0));

export default function PlanningPanel() {
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [team, setTeam] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [moveForm, setMoveForm] = useState({ date: "", time: "" });
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [blockForm, setBlockForm] = useState(null);
  const [rdvForm, setRdvForm] = useState(null);

  const load = () => {
    api.get("/admin/bookings").then((r) => setBookings(r.data.bookings)).catch(() => {});
    api.get("/admin/blocks").then((r) => setBlocks(r.data.blocks)).catch(() => {});
    api.get("/team").then((r) => setTeam(r.data.team)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data.categories.flatMap((c) => c.services))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const navigate = (dir) => {
    if (view === "day") setCursor(addDays(cursor, dir));
    else if (view === "week") setCursor(addWeeks(cursor, dir));
    else setCursor(addMonths(cursor, dir));
  };

  const itemsFor = (d) => {
    const key = toKey(d);
    return {
      bookings: bookings.filter((b) => b.date === key).sort((a, b) => a.time.localeCompare(b.time)),
      blocks: blocks.filter((b) => b.date === key),
    };
  };

  const weekDays = useMemo(() => {
    const start = startOfWeek(cursor, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [cursor]);

  const monthDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    const days = [];
    for (let d = start; d <= end; d = addDays(d, 1)) days.push(d);
    return days;
  }, [cursor]);

  const Chip = ({ b }) => (
    <button onClick={() => { setSelectedBooking(b); setMoveForm({ date: b.date, time: b.time }); }}
            className={`block w-full rounded px-2 py-1.5 text-left text-[11px] font-light leading-tight transition-transform duration-150 hover:scale-[0.98] ${STATUS_CHIP[b.status]}`}
            data-testid={`planning-booking-${b.id}`}>
      <span className="font-medium">{b.time}</span> · {b.client_name}
      <span className="block truncate opacity-75">{b.service_name}{b.staff_name ? ` · ${b.staff_name}` : ""}</span>
    </button>
  );

  const BlockChip = ({ b }) => (
    <button onClick={() => setSelectedBlock(b)}
            className="block w-full rounded bg-[var(--brand-dark)] px-2 py-1.5 text-left text-[11px] font-light leading-tight text-[var(--brand-bg)] transition-transform duration-150 hover:scale-[0.98]"
            data-testid={`planning-block-${b.id}`}>
      <Ban size={10} className="mr-1 inline" />
      {b.full_day ? "Journée bloquée" : `Bloqué ${b.start_time}–${b.end_time}`}
    </button>
  );

  const DayColumn = ({ d, large }) => {
    const { bookings: bs, blocks: bls } = itemsFor(d);
    return (
      <div className={`min-h-32 border-b border-r border-black/5 p-2 last:border-r-0 ${isSameDay(d, new Date()) ? "bg-[var(--brand-accent)]/10" : ""} ${large ? "" : ""}`}
           data-testid={`planning-day-${toKey(d)}`}>
        <p className="mb-2 text-xs uppercase tracking-[0.15em] text-[var(--brand-muted)]">
          {format(d, large ? "EEEE d MMMM" : "EEE d", { locale: fr })}
        </p>
        <div className="space-y-1.5">
          {bls.map((b) => <BlockChip key={b.id} b={b} />)}
          {bs.map((b) => <Chip key={b.id} b={b} />)}
          {bs.length === 0 && bls.length === 0 && <p className="text-[11px] italic text-[var(--brand-muted)]/60">—</p>}
        </div>
      </div>
    );
  };

  const saveBlock = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/blocks", blockForm);
      toast.success("Créneau bloqué — indisponible pour les clientes.");
      setBlockForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeBlock = async (b) => {
    await api.delete(`/admin/blocks/${b.id}`).catch(() => toast.error("Suppression impossible"));
    toast.success("Créneau débloqué.");
    setSelectedBlock(null);
    load();
  };

  const patchBooking = async (id, payload, successMsg) => {
    try {
      await api.patch(`/admin/bookings/${id}`, payload);
      toast.success(successMsg);
      setSelectedBooking(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveRdv = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/bookings", rdvForm);
      toast.success("Rendez-vous ajouté au planning.");
      setRdvForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const inputCls = "mt-1 h-11 w-full rounded-none border border-black/10 bg-transparent text-sm font-light px-3";
  const labelCls = "text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]";
  const staffForService = (serviceId) => {
    const svc = services.find((s) => s.id === serviceId);
    return svc ? team.filter((m) => m.specialties.includes(svc.category)) : [];
  };

  return (
    <div className="mt-10" data-testid="planning-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-display text-3xl text-[var(--brand-dark)]">Planning</h2>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => setRdvForm({ service_id: "", staff_id: "", date: toKey(cursor), time: "10:00", client_name: "", email: "", phone: "" })}
                  className="flex items-center gap-2 rounded-full bg-[var(--brand-dark)] px-5 py-2.5 text-[11px] uppercase tracking-[0.15em] text-[var(--brand-bg)] transition-transform duration-200 hover:scale-95"
                  data-testid="planning-add-rdv-btn">
            <Plus size={13} /> Nouveau RDV
          </button>
          <button onClick={() => setBlockForm({ date: toKey(cursor), start_time: "12:00", end_time: "14:00", full_day: false, staff_id: "", reason: "" })}
                  className="flex items-center gap-2 rounded-full border border-[var(--brand-dark)] px-5 py-2.5 text-[11px] uppercase tracking-[0.15em] text-[var(--brand-dark)] transition-colors duration-200 hover:bg-[var(--brand-dark)] hover:text-[var(--brand-bg)]"
                  data-testid="planning-block-btn">
            <Ban size={13} /> Bloquer un créneau
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="rounded-full border border-black/10 p-2 text-[var(--brand-dark)] hover:border-[var(--brand-accent)]" data-testid="planning-prev"><ChevronLeft size={16} /></button>
          <button onClick={() => setCursor(new Date())} className="rounded-full border border-black/10 px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-[var(--brand-dark)] hover:border-[var(--brand-accent)]" data-testid="planning-today">Aujourd'hui</button>
          <button onClick={() => navigate(1)} className="rounded-full border border-black/10 p-2 text-[var(--brand-dark)] hover:border-[var(--brand-accent)]" data-testid="planning-next"><ChevronRight size={16} /></button>
          <p className="ml-3 font-display text-xl capitalize text-[var(--brand-dark)]" data-testid="planning-title">
            {view === "month" ? format(cursor, "MMMM yyyy", { locale: fr }) : format(cursor, "d MMMM yyyy", { locale: fr })}
          </p>
        </div>
        <div className="flex gap-2" data-testid="planning-views">
          {[["day", "Jour"], ["week", "Semaine"], ["month", "Mois"]].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)}
                    className={`rounded-full border px-5 py-2 text-[11px] uppercase tracking-[0.15em] transition-colors duration-200 ${
                      view === k ? "border-[var(--brand-dark)] bg-[var(--brand-dark)] text-[var(--brand-bg)]" : "border-black/15 text-[var(--brand-dark)] hover:border-[var(--brand-accent)]"
                    }`}
                    data-testid={`planning-view-${k}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 overflow-x-auto border border-black/5 bg-[var(--brand-bg)]" data-testid="planning-calendar">
        {view === "day" && <DayColumn d={cursor} large />}
        {view === "week" && (
          <div className="grid min-w-[900px] grid-cols-7">
            {weekDays.map((d) => <DayColumn key={toKey(d)} d={d} />)}
          </div>
        )}
        {view === "month" && (
          <div className="grid min-w-[900px] grid-cols-7">
            {monthDays.map((d) => {
              const { bookings: bs, blocks: bls } = itemsFor(d);
              return (
                <button key={toKey(d)} onClick={() => { setCursor(d); setView("day"); }}
                        className={`min-h-24 border-b border-r border-black/5 p-2 text-left align-top transition-colors hover:bg-[var(--brand-accent)]/10 ${!isSameMonth(d, cursor) ? "opacity-35" : ""} ${isSameDay(d, new Date()) ? "bg-[var(--brand-accent)]/10" : ""}`}
                        data-testid={`planning-month-day-${toKey(d)}`}>
                  <p className="text-xs font-light text-[var(--brand-muted)]">{format(d, "d")}</p>
                  {(bs.length + bls.length) > 0 && (
                    <p className="mt-1 text-[11px] font-medium text-[var(--brand-accent)]">
                      {bs.length > 0 && `${bs.length} RDV`}{bs.length > 0 && bls.length > 0 && " · "}{bls.length > 0 && `${bls.length} blocage${bls.length > 1 ? "s" : ""}`}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-5 text-[11px] uppercase tracking-[0.15em] text-[var(--brand-muted)]">
        <span className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-[var(--brand-accent)]/40" /> En attente</span>
        <span className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-[#336633]/30" /> Confirmé</span>
        <span className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-[var(--brand-dark)]" /> Bloqué</span>
      </div>

      <Dialog open={!!selectedBooking} onOpenChange={(o) => !o && setSelectedBooking(null)}>
        <DialogContent className="bg-[var(--brand-bg)] sm:max-w-lg" data-testid="planning-booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-normal text-[var(--brand-dark)]">Rendez-vous</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="grid gap-4 text-sm font-light text-[var(--brand-dark)]">
              <div className="border border-black/10 p-4">
                <p className="font-medium">{selectedBooking.client_name}</p>
                <p className="text-[var(--brand-muted)]">{selectedBooking.email} · {selectedBooking.phone}</p>
                <p className="mt-2">{selectedBooking.service_name}{selectedBooking.staff_name ? ` · avec ${selectedBooking.staff_name}` : ""}</p>
                <p className="text-[var(--brand-muted)]">{selectedBooking.date} à {selectedBooking.time} · {formatDuration(selectedBooking.duration)} · {selectedBooking.price} €</p>
                <div className="mt-3 flex gap-2">
                  <span className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.15em] ${STATUS_CHIP[selectedBooking.status]}`}>{STATUS_LABELS[selectedBooking.status]}</span>
                  {(selectedBooking.deposit_amount || 0) > 0 && (
                    <span className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.15em] ${PAYMENT_STYLES[selectedBooking.payment_status || "none"]}`}>
                      {PAYMENT_LABELS[selectedBooking.payment_status || "none"]} · {selectedBooking.deposit_amount} €
                    </span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Déplacer — date</label>
                  <Input type="date" value={moveForm.date} onChange={(e) => setMoveForm({ ...moveForm, date: e.target.value })} className={inputCls} data-testid="move-date" />
                </div>
                <div>
                  <label className={labelCls}>Heure</label>
                  <Input type="time" value={moveForm.time} onChange={(e) => setMoveForm({ ...moveForm, time: e.target.value })} className={inputCls} data-testid="move-time" />
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => patchBooking(selectedBooking.id, { date: moveForm.date, time: moveForm.time }, "Rendez-vous déplacé.")}
                        className="rounded-full border border-black/15 px-5 py-2.5 text-[11px] uppercase tracking-[0.15em] text-[var(--brand-dark)] hover:border-[var(--brand-accent)]" data-testid="move-save-btn">
                  Déplacer
                </button>
                {selectedBooking.status === "pending" && (
                  <button onClick={() => patchBooking(selectedBooking.id, { status: "confirmed" }, "Rendez-vous confirmé.")}
                          className="rounded-full bg-[var(--brand-dark)] px-5 py-2.5 text-[11px] uppercase tracking-[0.15em] text-[var(--brand-bg)]" data-testid="dialog-confirm-btn">
                    Confirmer
                  </button>
                )}
                {selectedBooking.status !== "cancelled" && (
                  <button onClick={() => patchBooking(selectedBooking.id, { status: "cancelled" }, "Rendez-vous annulé.")}
                          className="rounded-full border border-[#993333]/30 px-5 py-2.5 text-[11px] uppercase tracking-[0.15em] text-[#993333] hover:bg-[#993333] hover:text-white" data-testid="dialog-cancel-btn">
                    Annuler
                  </button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedBlock} onOpenChange={(o) => !o && setSelectedBlock(null)}>
        <DialogContent className="bg-[var(--brand-bg)] sm:max-w-md" data-testid="planning-block-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-normal text-[var(--brand-dark)]">Créneau bloqué</DialogTitle>
          </DialogHeader>
          {selectedBlock && (
            <div className="grid gap-4 text-sm font-light text-[var(--brand-dark)]">
              <p>{selectedBlock.date} — {selectedBlock.full_day ? "toute la journée" : `${selectedBlock.start_time} à ${selectedBlock.end_time}`}</p>
              {selectedBlock.reason && <p className="italic text-[var(--brand-muted)]">Motif privé : {selectedBlock.reason}</p>}
              <button onClick={() => removeBlock(selectedBlock)}
                      className="flex items-center justify-center gap-2 rounded-full bg-[var(--brand-dark)] py-3 text-xs uppercase tracking-[0.2em] text-[var(--brand-bg)]" data-testid="block-delete-btn">
                <Trash2 size={13} /> Débloquer ce créneau
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!blockForm} onOpenChange={(o) => !o && setBlockForm(null)}>
        <DialogContent className="bg-[var(--brand-bg)] sm:max-w-lg" data-testid="block-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-normal text-[var(--brand-dark)]">Bloquer un créneau</DialogTitle>
          </DialogHeader>
          {blockForm && (
            <form onSubmit={saveBlock} className="grid gap-4">
              <div>
                <label className={labelCls}>Date</label>
                <Input required type="date" value={blockForm.date} onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })} className={inputCls} data-testid="block-date" />
              </div>
              <label className="flex cursor-pointer items-center gap-3">
                <input type="checkbox" checked={blockForm.full_day} onChange={(e) => setBlockForm({ ...blockForm, full_day: e.target.checked })}
                       className="h-4 w-4 accent-[#C4839B]" data-testid="block-fullday" />
                <span className="text-sm font-light text-[var(--brand-dark)]">Bloquer toute la journée</span>
              </label>
              {!blockForm.full_day && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Début</label>
                    <Input required type="time" value={blockForm.start_time} onChange={(e) => setBlockForm({ ...blockForm, start_time: e.target.value })} className={inputCls} data-testid="block-start" />
                  </div>
                  <div>
                    <label className={labelCls}>Fin</label>
                    <Input required type="time" value={blockForm.end_time} onChange={(e) => setBlockForm({ ...blockForm, end_time: e.target.value })} className={inputCls} data-testid="block-end" />
                  </div>
                </div>
              )}
              <div>
                <label className={labelCls}>Professionnelle concernée</label>
                <Select value={blockForm.staff_id || "all"} onValueChange={(v) => setBlockForm({ ...blockForm, staff_id: v === "all" ? "" : v })}>
                  <SelectTrigger className="mt-1 h-11 rounded-none border-black/10 bg-transparent font-light" data-testid="block-staff"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="block-staff-all">Toutes (tout l'institut)</SelectItem>
                    {team.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`block-staff-${m.id}`}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelCls}>Motif privé (invisible pour les clientes)</label>
                <Input value={blockForm.reason} onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })}
                       placeholder="Pause, rendez-vous personnel…" className={inputCls} data-testid="block-reason" />
              </div>
              <button type="submit" className="rounded-full bg-[var(--brand-dark)] py-4 text-xs uppercase tracking-[0.25em] text-[var(--brand-bg)]" data-testid="block-save-btn">
                Bloquer ce créneau
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!rdvForm} onOpenChange={(o) => !o && setRdvForm(null)}>
        <DialogContent className="bg-[var(--brand-bg)] sm:max-w-lg" data-testid="rdv-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-normal text-[var(--brand-dark)]">Nouveau rendez-vous manuel</DialogTitle>
          </DialogHeader>
          {rdvForm && (
            <form onSubmit={saveRdv} className="grid gap-4">
              <div>
                <label className={labelCls}>Prestation</label>
                <Select value={rdvForm.service_id} onValueChange={(v) => setRdvForm({ ...rdvForm, service_id: v, staff_id: "" })}>
                  <SelectTrigger className="mt-1 h-11 rounded-none border-black/10 bg-transparent font-light" data-testid="rdv-service"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>
                    {services.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`rdv-service-${s.id}`}>{s.name} — {s.price} €</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelCls}>Professionnelle</label>
                <Select value={rdvForm.staff_id} onValueChange={(v) => setRdvForm({ ...rdvForm, staff_id: v })}>
                  <SelectTrigger className="mt-1 h-11 rounded-none border-black/10 bg-transparent font-light" data-testid="rdv-staff"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>
                    {staffForService(rdvForm.service_id).map((m) => <SelectItem key={m.id} value={m.id} data-testid={`rdv-staff-${m.id}`}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Date</label>
                  <Input required type="date" value={rdvForm.date} onChange={(e) => setRdvForm({ ...rdvForm, date: e.target.value })} className={inputCls} data-testid="rdv-date" />
                </div>
                <div>
                  <label className={labelCls}>Heure</label>
                  <Input required type="time" value={rdvForm.time} onChange={(e) => setRdvForm({ ...rdvForm, time: e.target.value })} className={inputCls} data-testid="rdv-time" />
                </div>
              </div>
              <Input required placeholder="Nom de la cliente" value={rdvForm.client_name} onChange={(e) => setRdvForm({ ...rdvForm, client_name: e.target.value })} className={inputCls} data-testid="rdv-name" />
              <div className="grid grid-cols-2 gap-4">
                <Input placeholder="Email (optionnel)" type="email" value={rdvForm.email} onChange={(e) => setRdvForm({ ...rdvForm, email: e.target.value })} className={inputCls} data-testid="rdv-email" />
                <Input placeholder="Téléphone" value={rdvForm.phone} onChange={(e) => setRdvForm({ ...rdvForm, phone: e.target.value })} className={inputCls} data-testid="rdv-phone" />
              </div>
              <button type="submit" className="rounded-full bg-[var(--brand-dark)] py-4 text-xs uppercase tracking-[0.25em] text-[var(--brand-bg)]" data-testid="rdv-save-btn">
                Ajouter au planning
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
